/**
 * ═══════════════════════════════════════════════════════════════════════════
 * StarGateApp.tsx - Portal Unificado Multi-Canal
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 2.0.0
 * @date 2025-01-27
 * 
 * TABS:
 * - 📊 Dashboard: Métricas y stats
 * - 📷 Instagram: Conversaciones Instagram
 * - 📘 Facebook: Conversaciones Facebook
 * - ✈️ Telegram: Conversaciones Telegram
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { Card, Row, Col, Badge, Button, Form, InputGroup, Nav, ProgressBar, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDoorOpen,
  faCamera,
  faShare,
  faPaperPlane,
  faChartLine,
  faSearch,
  faClock,
  faCheckDouble,
  faArrowUp,
  faArrowDown,
  faMinus,
  faFire,
  faUsers,
  faBolt,
  faComment
} from '@fortawesome/free-solid-svg-icons';

type ChannelType = 'instagram' | 'facebook' | 'telegram';
type TabType = 'dashboard' | ChannelType;

interface ChannelStats {
  totalMessages: number;
  unreadCount: number;
  activeConversations: number;
  avgResponseTime: number;
  todayTrend: number;
  responseRate: number;
}

interface Conversation {
  id: string;
  contactId: string;
  contactName: string;
  lastMessage: string;
  timestamp: string;
  unread: boolean;
  messageCount: number;
  fromMe: boolean;
}

interface ChannelConfig {
  icon: any;
  color: string;
  label: string;
  bgColor: string;
}

interface TopContact {
  name: string;
  channel: string;
  messages: number;
  trend: string;
}

const CHANNEL_CONFIGS: Record<ChannelType, ChannelConfig> = {
  instagram: {
    icon: faCamera,
    color: '#8b5cf6',
    label: 'Instagram',
    bgColor: 'rgba(139, 92, 246, 0.1)'
  },
  facebook: {
    icon: faShare,
    color: '#2ca799',
    label: 'Facebook',
    bgColor: 'rgba(44, 167, 153, 0.1)'
  },
  telegram: {
    icon: faPaperPlane,
    color: '#6366f1',
    label: 'Telegram',
    bgColor: 'rgba(99, 102, 241, 0.1)'
  }
};

const MOCK_STATS: Record<ChannelType, ChannelStats> = {
  instagram: { totalMessages: 234, unreadCount: 12, activeConversations: 8, avgResponseTime: 3.5, todayTrend: 15, responseRate: 94.2 },
  facebook: { totalMessages: 89, unreadCount: 3, activeConversations: 4, avgResponseTime: 5.2, todayTrend: -5, responseRate: 88.5 },
  telegram: { totalMessages: 45, unreadCount: 0, activeConversations: 2, avgResponseTime: 2.1, todayTrend: 0, responseRate: 96.8 }
};

const MOCK_CONVERSATIONS: Record<ChannelType, Conversation[]> = {
  instagram: [
    { id: '1', contactId: '@juanperez', contactName: 'Juan Pérez', lastMessage: 'Hola, vi su producto en Instagram', timestamp: new Date(Date.now() - 5 * 60000).toISOString(), unread: true, messageCount: 5, fromMe: false },
    { id: '2', contactId: '@maria_lopez', contactName: 'María López', lastMessage: 'Gracias por la info!', timestamp: new Date(Date.now() - 2 * 3600000).toISOString(), unread: false, messageCount: 8, fromMe: false }
  ],
  facebook: [
    { id: '4', contactId: 'ana.gonzalez.fb', contactName: 'Ana González', lastMessage: '¿Cuál es el precio?', timestamp: new Date(Date.now() - 30 * 60000).toISOString(), unread: true, messageCount: 3, fromMe: false }
  ],
  telegram: [
    { id: '6', contactId: '@sofia_dev', contactName: 'Sofía Dev', lastMessage: 'Excelente servicio!', timestamp: new Date(Date.now() - 6 * 3600000).toISOString(), unread: false, messageCount: 10, fromMe: false }
  ]
};

const TOP_CONTACTS: TopContact[] = [
  { name: '@juanperez', channel: 'instagram', messages: 15, trend: 'up' },
  { name: 'Ana González', channel: 'facebook', messages: 8, trend: 'up' },
  { name: '@sofia_dev', channel: 'telegram', messages: 6, trend: 'down' }
];

const StarGateApp: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);

  const getTimeAgo = (timestamp: string): string => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes} min`;
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(diff / 86400000)}d`;
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return { icon: faArrowUp, color: '#2ca799' };
    if (trend < 0) return { icon: faArrowDown, color: '#ef4444' };
    return { icon: faMinus, color: 'var(--bs-secondary)' };
  };

  const renderDashboard = () => {
    const totalStats = {
      messages: Object.values(MOCK_STATS).reduce((sum, s) => sum + s.totalMessages, 0),
      unread: Object.values(MOCK_STATS).reduce((sum, s) => sum + s.unreadCount, 0),
      active: Object.values(MOCK_STATS).reduce((sum, s) => sum + s.activeConversations, 0),
      avgResponse: (Object.values(MOCK_STATS).reduce((sum, s) => sum + s.avgResponseTime, 0) / 3).toFixed(1)
    };

    return (
      <div>
        <Row className="g-3 mb-4">
          {(Object.entries(MOCK_STATS) as [ChannelType, ChannelStats][]).map(([channel, stats]) => {
            const config = CHANNEL_CONFIGS[channel];
            const trend = getTrendIcon(stats.todayTrend);
            return (
              <Col key={channel} md={4}>
                <Card onClick={() => setActiveTab(channel)} style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'var(--bs-body-bg)', cursor: 'pointer', transition: 'all 0.2s ease' }} className="channel-stat-card">
                  <Card.Body style={{ padding: '20px' }}>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: `${config.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: config.color }}>
                        <FontAwesomeIcon icon={config.icon} />
                      </div>
                      <Badge bg="light" text="dark" style={{ fontSize: '0.7rem', padding: '4px 8px' }}>
                        <FontAwesomeIcon icon={trend.icon} style={{ color: trend.color }} className="me-1" />
                        {Math.abs(stats.todayTrend)}% hoy
                      </Badge>
                    </div>
                    <h6 style={{ fontWeight: 600, marginBottom: '16px', color: 'var(--bs-body-color)' }}>{config.label}</h6>
                    <Row className="g-3">
                      <Col xs={6}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 600, color: config.color }}>{stats.totalMessages}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>Mensajes</div>
                      </Col>
                      <Col xs={6}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#f59e0b' }}>{stats.unreadCount}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>Sin leer</div>
                      </Col>
                      <Col xs={6}>
                        <div style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--bs-body-color)' }}>{stats.avgResponseTime} min</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)' }}>Tiempo resp.</div>
                      </Col>
                      <Col xs={6}>
                        <div style={{ fontSize: '1rem', fontWeight: 500, color: '#2ca799' }}>{stats.responseRate}%</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)' }}>Tasa resp.</div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        <Row className="g-3 mb-4">
          <Col md={3}>
            <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'var(--bs-body-bg)', textAlign: 'center', padding: '20px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#8b5cf620', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: '#8b5cf6', margin: '0 auto 12px' }}>
                <FontAwesomeIcon icon={faComment} />
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 600, color: 'var(--bs-body-color)', marginBottom: '4px' }}>{totalStats.messages}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>Total Mensajes</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'var(--bs-body-bg)', textAlign: 'center', padding: '20px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#f59e0b20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: '#f59e0b', margin: '0 auto 12px' }}>
                <FontAwesomeIcon icon={faBolt} />
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 600, color: 'var(--bs-body-color)', marginBottom: '4px' }}>{totalStats.unread}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>Sin Leer</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'var(--bs-body-bg)', textAlign: 'center', padding: '20px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#2ca79920', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: '#2ca799', margin: '0 auto 12px' }}>
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 600, color: 'var(--bs-body-color)', marginBottom: '4px' }}>{totalStats.active}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>Activas</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'var(--bs-body-bg)', textAlign: 'center', padding: '20px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#6366f120', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: '#6366f1', margin: '0 auto 12px' }}>
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 600, color: 'var(--bs-body-color)', marginBottom: '4px' }}>{totalStats.avgResponse}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>Min Promedio</div>
            </Card>
          </Col>
        </Row>

        <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'var(--bs-body-bg)' }}>
          <Card.Header style={{ backgroundColor: 'var(--bs-body-bg)', borderBottom: '1px solid var(--bs-border-color)', padding: '16px 20px' }}>
            <h6 style={{ fontWeight: 600, marginBottom: 0 }}>
              <FontAwesomeIcon icon={faFire} className="me-2" style={{ color: '#f59e0b' }} />
              Contactos Más Activos Hoy
            </h6>
          </Card.Header>
          <Card.Body style={{ padding: '0' }}>
            <Table hover size="sm" style={{ marginBottom: 0 }}>
              <tbody>
                {TOP_CONTACTS.map((contact, idx) => {
                  const config = CHANNEL_CONFIGS[contact.channel as ChannelType];
                  return (
                    <tr key={idx} style={{ borderBottom: idx < TOP_CONTACTS.length - 1 ? '1px solid var(--bs-border-color)' : 'none' }}>
                      <td style={{ width: '48px', padding: '12px 20px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: `${config.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: config.color }}>
                          <FontAwesomeIcon icon={config.icon} />
                        </div>
                      </td>
                      <td style={{ padding: '12px 8px' }}><strong style={{ color: 'var(--bs-body-color)' }}>{contact.name}</strong></td>
                      <td style={{ color: 'var(--bs-secondary)', fontSize: '0.85rem', padding: '12px 8px' }}>{config.label}</td>
                      <td className="text-end" style={{ padding: '12px 8px' }}>
                        <Badge bg="light" text="dark" style={{ padding: '4px 8px', fontSize: '0.75rem' }}>
                          {contact.messages} mensajes
                        </Badge>
                      </td>
                      <td style={{ width: '48px', textAlign: 'center', padding: '12px 20px' }}>
                        <FontAwesomeIcon icon={contact.trend === 'up' ? faArrowUp : faArrowDown} style={{ color: contact.trend === 'up' ? '#2ca799' : '#ef4444' }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderChannelConversations = (channel: ChannelType) => {
    const config = CHANNEL_CONFIGS[channel];
    const stats = MOCK_STATS[channel];
    let conversations = MOCK_CONVERSATIONS[channel] || [];

    if (filterUnread) conversations = conversations.filter(c => c.unread);
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      conversations = conversations.filter(c => c.contactName.toLowerCase().includes(query) || c.contactId.toLowerCase().includes(query) || c.lastMessage.toLowerCase().includes(query));
    }

    return (
      <div>
        <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'var(--bs-body-bg)', marginBottom: '20px' }}>
          <Card.Body style={{ padding: '20px' }}>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: '56px', height: '56px', borderRadius: '12px', backgroundColor: `${config.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', color: config.color }}>
                    <FontAwesomeIcon icon={config.icon} />
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 600, color: 'var(--bs-body-color)' }}>{config.label}</h5>
                    <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginTop: '4px' }}>
                      {stats.activeConversations} conversaciones activas · {stats.unreadCount} sin leer
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginBottom: '6px' }}>Tasa de respuesta</div>
                <ProgressBar now={stats.responseRate} label={`${stats.responseRate}%`} style={{ height: '20px', borderRadius: '6px', backgroundColor: 'var(--bs-border-color)' }} variant="success" />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row className="g-2 mb-3">
          <Col md={8}>
            <InputGroup>
              <InputGroup.Text style={{ backgroundColor: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)' }}>
                <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--bs-secondary)' }} />
              </InputGroup.Text>
              <Form.Control type="text" placeholder="Buscar por nombre o mensaje..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ border: '1px solid var(--bs-border-color)' }} />
            </InputGroup>
          </Col>
          <Col md={4}>
            <Button variant={filterUnread ? 'primary' : 'outline-secondary'} onClick={() => setFilterUnread(!filterUnread)} style={{ width: '100%', borderRadius: '6px' }}>
              {filterUnread ? '📥 Sin leer' : '📬 Todos'}
            </Button>
          </Col>
        </Row>

        {conversations.length === 0 ? (
          <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'var(--bs-body-bg)', padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.2, color: config.color }}>
              <FontAwesomeIcon icon={config.icon} />
            </div>
            <h6 style={{ color: 'var(--bs-secondary)', fontWeight: 500 }}>
              {searchQuery || filterUnread ? 'No se encontraron conversaciones' : 'No hay conversaciones aún'}
            </h6>
          </Card>
        ) : (
          <div className="d-flex flex-column gap-2">
            {conversations.map(conv => (
              <Card key={conv.id} onClick={() => alert(`Conversación: ${conv.contactName}`)} style={{ border: conv.unread ? `1px solid ${config.color}` : 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'all 0.2s ease', backgroundColor: 'var(--bs-body-bg)' }} className="conversation-card">
                <Card.Body style={{ padding: '16px' }}>
                  <div className="d-flex justify-content-between">
                    <div style={{ flex: 1 }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h6 style={{ margin: 0, fontWeight: conv.unread ? 600 : 500, color: 'var(--bs-body-color)' }}>{conv.contactName}</h6>
                        {conv.unread && <Badge bg="danger" pill style={{ fontSize: '0.65rem', padding: '2px 6px' }}>NUEVO</Badge>}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary)', marginBottom: '8px' }}>{conv.contactId}</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conv.fromMe && <FontAwesomeIcon icon={faCheckDouble} className="me-2" style={{ color: config.color }} />}
                        {conv.lastMessage}
                      </div>
                    </div>
                    <div className="text-end ms-3" style={{ minWidth: '80px' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary)', marginBottom: '6px' }}>
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        {getTimeAgo(conv.timestamp)}
                      </div>
                      <Badge bg="light" text="dark" style={{ fontSize: '0.65rem', padding: '3px 6px' }}>
                        {conv.messageCount} msgs
                      </Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'var(--bs-body-bg)' }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--bs-border-color)', backgroundColor: 'var(--bs-body-bg)' }}>
        <div className="mb-3">
          <h4 style={{ margin: 0, fontWeight: 600, color: 'var(--bs-body-color)' }}>
            <FontAwesomeIcon icon={faDoorOpen} className="me-2" style={{ color: '#8b5cf6' }} />
            StarGate
          </h4>
          <p style={{ margin: '4px 0 0 0', color: 'var(--bs-secondary)', fontSize: '0.85rem' }}>
            Portal unificado multi-canal
          </p>
        </div>

        <Nav variant="tabs" style={{ borderBottom: 'none', gap: '8px' }}>
          <Nav.Item>
            <Nav.Link active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} style={{ cursor: 'pointer', padding: '10px 16px', border: 'none', borderRadius: '8px 8px 0 0', backgroundColor: activeTab === 'dashboard' ? 'var(--bs-body-bg)' : 'transparent', borderBottom: activeTab === 'dashboard' ? '2px solid #8b5cf6' : '2px solid transparent', color: activeTab === 'dashboard' ? '#8b5cf6' : 'var(--bs-secondary)', fontWeight: activeTab === 'dashboard' ? 600 : 400, fontSize: '0.9rem' }}>
              <FontAwesomeIcon icon={faChartLine} className="me-2" />Dashboard
            </Nav.Link>
          </Nav.Item>

          {(Object.entries(CHANNEL_CONFIGS) as [ChannelType, ChannelConfig][]).map(([channel, config]) => {
            const stats = MOCK_STATS[channel];
            return (
              <Nav.Item key={channel}>
                <Nav.Link active={activeTab === channel} onClick={() => setActiveTab(channel)} style={{ cursor: 'pointer', padding: '10px 16px', border: 'none', borderRadius: '8px 8px 0 0', backgroundColor: activeTab === channel ? 'var(--bs-body-bg)' : 'transparent', borderBottom: activeTab === channel ? `2px solid ${config.color}` : '2px solid transparent', color: activeTab === channel ? config.color : 'var(--bs-secondary)', fontWeight: activeTab === channel ? 600 : 400, fontSize: '0.9rem' }}>
                  <FontAwesomeIcon icon={config.icon} className="me-2" />{config.label}
                  {stats.unreadCount > 0 && <Badge bg="danger" className="ms-2" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>{stats.unreadCount}</Badge>}
                </Nav.Link>
              </Nav.Item>
            );
          })}
        </Nav>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab !== 'dashboard' && renderChannelConversations(activeTab as ChannelType)}
      </div>

      <style>{`
        .channel-stat-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
        .conversation-card:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
      `}</style>
    </div>
  );
};

export default StarGateApp;